import type * as React from 'react';

import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../lib/utils';

const badgeVariants = cva(
    'inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md border px-1.5 pt-0.5 pb-0.75 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus-visible:border-ring [a&]:cursor-pointer [button&]:cursor-pointer [button&]:disabled:cursor-not-allowed [button&]:disabled:opacity-50',
    {
        variants: {
            variant: {
                default:
                    'border-transparent bg-primary text-primary-foreground hover:bg-primary/80',
                secondary:
                    'border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80',
                secondarySoft:
                    'border-transparent bg-secondary-background text-secondary-text hover:bg-secondary-background/80',
                destructive:
                    'border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80',
                destructiveSoft:
                    'border-transparent bg-destructive-foreground text-destructive hover:bg-destructive-foreground/80',
                outline: 'border-border bg-background text-foreground hover:border-border/70',
                success:
                    'border-transparent bg-success text-success-foreground hover:bg-success/80 hover:text-success-foreground/70',
                warning:
                    'border-transparent bg-badge-warning text-warning-foreground hover:bg-badge-warning/80 hover:text-warning-foreground/70',
                info: 'border-transparent bg-info text-info-foreground hover:bg-info/80 hover:text-info-foreground/70',
            },
        },
        defaultVariants: {
            variant: 'default',
        },
    },
);

export interface BadgeProps
    extends React.HTMLAttributes<HTMLDivElement>,
        VariantProps<typeof badgeVariants> {
    asChild?: boolean;
}

function Badge({ className, variant, asChild = false, ...props }: BadgeProps) {
    const Comp = asChild ? Slot : 'div';
    return <Comp className={cn(badgeVariants({ variant }), className)} {...props} />;
}

export { Badge, badgeVariants };
